import { useState } from 'react'
import { Play, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import useSipStore from '../stores/sipStore'

/**
 * Built-in diagnostics that run from the browser. Each test reports
 * pass/fail with a short message so it's clear what's broken.
 */
const TESTS = [
  {
    id: 'dns',
    name: 'DNS resolution',
    run: async () => {
      // Fetch the WSS host's TLS cert URL via HEAD - confirms DNS + TLS at least.
      const url = `https://${useSipStore.getState().config.domain}/health`
      try {
        const res = await fetch(url, { method: 'HEAD' }).catch(() => fetch(url))
        return { ok: true, msg: `HTTP ${res.status} from ${url}` }
      } catch (e) {
        return { ok: false, msg: `Cannot reach ${url}: ${e.message}` }
      }
    },
  },
  {
    id: 'wss',
    name: 'WebSocket TLS handshake',
    run: async () => {
      const wssUrl = useSipStore.getState().config.wssUrl
      return new Promise((resolve) => {
        try {
          const ws = new WebSocket(wssUrl, ['sip'])
          const timer = setTimeout(() => {
            try { ws.close() } catch {}
            resolve({ ok: false, msg: 'Timeout (5s) - no WebSocket response' })
          }, 5000)
          ws.onopen = () => {
            clearTimeout(timer)
            const proto = ws.protocol || '(no subprotocol)'
            try { ws.close() } catch {}
            resolve({ ok: true, msg: `Connected, subprotocol=${proto}` })
          }
          ws.onerror = (e) => {
            clearTimeout(timer)
            resolve({ ok: false, msg: `WebSocket error - check console for CORS/TLS issues` })
          }
        } catch (e) {
          resolve({ ok: false, msg: e.message })
        }
      })
    },
  },
  {
    id: 'sip-options',
    name: 'SIP OPTIONS handshake',
    run: async () => {
      const { config } = useSipStore.getState()
      return new Promise((resolve) => {
        const ws = new WebSocket(config.wssUrl, ['sip'])
        const timer = setTimeout(() => {
          try { ws.close() } catch {}
          resolve({ ok: false, msg: 'Timeout - no SIP response within 5s' })
        }, 5000)
        ws.onopen = () => {
          const callId = 'test-' + Math.random().toString(36).slice(2, 10)
          const branch = 'z9hG4bK-' + Math.random().toString(36).slice(2, 10)
          const msg =
            `OPTIONS sip:${config.domain} SIP/2.0\r\n` +
            `Via: SIP/2.0/WSS test.invalid;branch=${branch}\r\n` +
            `Max-Forwards: 70\r\n` +
            `To: <sip:${config.domain}>\r\n` +
            `From: <sip:test@${config.domain}>;tag=test\r\n` +
            `Call-ID: ${callId}\r\n` +
            `CSeq: 1 OPTIONS\r\n` +
            `User-Agent: KaliDialer-Test/1.0\r\n` +
            `Content-Length: 0\r\n\r\n`
          ws.send(msg)
        }
        ws.onmessage = (evt) => {
          clearTimeout(timer)
          const txt = String(evt.data)
          const firstLine = txt.split('\r\n')[0]
          try { ws.close() } catch {}
          if (firstLine.includes('200') || firstLine.includes('OK')) {
            resolve({ ok: true, msg: firstLine })
          } else {
            resolve({ ok: true, msg: `Got: ${firstLine}` })
          }
        }
        ws.onerror = () => {
          clearTimeout(timer)
          resolve({ ok: false, msg: 'WS error during OPTIONS' })
        }
      })
    },
  },
  {
    id: 'mic-permission',
    name: 'Microphone permission',
    run: async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        const tracks = stream.getAudioTracks().length
        stream.getTracks().forEach(t => t.stop())
        return { ok: tracks > 0, msg: tracks > 0 ? `${tracks} audio track(s) available` : 'No audio tracks' }
      } catch (e) {
        return { ok: false, msg: e.message }
      }
    },
  },
  {
    id: 'webrtc',
    name: 'WebRTC support',
    run: async () => {
      const ok = !!(window.RTCPeerConnection && navigator.mediaDevices?.getUserMedia)
      return { ok, msg: ok ? 'RTCPeerConnection + getUserMedia available' : 'Browser missing WebRTC APIs' }
    },
  },
  {
    id: 'register',
    name: 'SIP REGISTER (full)',
    run: async () => {
      // This actually triggers the full registration via JsSIP.
      // It waits up to 10s and reports the final status.
      const store = useSipStore.getState()
      if (store.status === 'registered') {
        return { ok: true, msg: 'Already registered ✓' }
      }
      store.connect()
      return new Promise((resolve) => {
        const start = Date.now()
        const checker = setInterval(() => {
          const s = useSipStore.getState()
          if (s.status === 'registered') {
            clearInterval(checker)
            resolve({ ok: true, msg: `Registered in ${Date.now() - start}ms` })
          } else if (s.status === 'failed') {
            clearInterval(checker)
            resolve({ ok: false, msg: s.error || 'Registration failed' })
          } else if (Date.now() - start > 12000) {
            clearInterval(checker)
            resolve({ ok: false, msg: `Timeout - stuck on status=${s.status}` })
          }
        }, 250)
      })
    },
  },
]

export default function TestRunner() {
  const [results, setResults] = useState({})
  const [running, setRunning] = useState(false)

  const runAll = async () => {
    setRunning(true)
    setResults({})
    for (const t of TESTS) {
      setResults(r => ({ ...r, [t.id]: { status: 'running' } }))
      try {
        const result = await t.run()
        setResults(r => ({ ...r, [t.id]: { status: result.ok ? 'pass' : 'fail', msg: result.msg } }))
      } catch (e) {
        setResults(r => ({ ...r, [t.id]: { status: 'fail', msg: e.message } }))
      }
    }
    setRunning(false)
  }

  return (
    <div className="bg-panel border border-line rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">בדיקות אבחון</h3>
        <button
          onClick={runAll}
          disabled={running}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand/90
            disabled:bg-line text-slate-900 rounded-lg text-sm font-medium"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {running ? 'רץ…' : 'הרץ הכל'}
        </button>
      </div>
      <div className="space-y-1.5">
        {TESTS.map(t => {
          const r = results[t.id]
          return (
            <div key={t.id} className="flex items-start gap-2 text-sm py-1">
              <div className="w-5 mt-0.5">
                {!r && <span className="text-mute">·</span>}
                {r?.status === 'running' && <Loader2 size={14} className="animate-spin text-mute" />}
                {r?.status === 'pass' && <CheckCircle2 size={14} className="text-ok" />}
                {r?.status === 'fail' && <XCircle size={14} className="text-err" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{t.name}</div>
                {r?.msg && (
                  <div className={`text-xs ltr tech ${r.status === 'fail' ? 'text-err' : 'text-mute'}`}>
                    {r.msg}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
